// WAAP Bot Management via the PUBLIC Open-API (reference slice).
//
// IMPORTANT: from the FE's side this looks EXACTLY like any other WAAP feature —
// same `/waap/domains/:id/...` shape, same hooks. The FE has no idea the
// servicemgmt BE serves this one off the public Open-API (root conversant SDK,
// HMAC) instead of the private SSO portal. That is the whole point of deciding the
// surface at the BE: the FE contract is stable regardless of surface.
//
// BE routes (servicemgmt eagle_waf/botopenapi):
//   GET    /waap/domains/:id/bot-openapi
//   POST   /waap/domains/:id/bot-openapi
//   GET    /waap/domains/:id/bot-openapi/:ruleId
//   PUT    /waap/domains/:id/bot-openapi/:ruleId
//   DELETE /waap/domains/:id/bot-openapi/:ruleId

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- DTOs (mirror the BE BotRuleDTO / BotRuleInput) ----------------------

export interface BotRule {
  id: number;
  name: string;
  description: string;
  silence_challenge_enabled: boolean;
  silence_challenge_action: string;
  ajax_enabled: boolean;
  anti_debugging_enabled: boolean;
  websites: number[];
}

export type BotRuleInput = Omit<BotRule, 'id'>;

export function emptyBotRule(): BotRuleInput {
  return {
    name: '',
    description: '',
    silence_challenge_enabled: false,
    silence_challenge_action: 'block',
    ajax_enabled: false,
    anti_debugging_enabled: false,
    websites: [],
  };
}

// ---- paths + keys --------------------------------------------------------

function botPath(domainId: string, sub = ''): string {
  return `/waap/domains/${domainId}/bot-openapi${sub}`;
}

export const botOpenApiKeys = {
  all: ['waap', 'bot-openapi'] as const,
  rules: (domainId: string) => ['waap', 'bot-openapi', domainId] as const,
};

function asArray<T = unknown>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const v = (raw as Record<string, unknown>).data;
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

// ---- hooks ---------------------------------------------------------------

export function useBotOpenApiRules(domainId: string | undefined) {
  return useQuery({
    queryKey: botOpenApiKeys.rules(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<BotRule[]> => {
      const res = await serviceApiV3.get<unknown>(botPath(domainId as string));
      return asArray<BotRule>(res.data);
    },
  });
}

export function useBotOpenApiRule(domainId: string | undefined, ruleId: string | undefined) {
  return useQuery({
    queryKey: [...botOpenApiKeys.rules(domainId ?? ''), ruleId ?? ''] as const,
    enabled: !!domainId && !!ruleId,
    retry: false,
    queryFn: async (): Promise<BotRule> => {
      const res = await serviceApiV3.get<BotRule>(botPath(domainId as string, `/${ruleId}`));
      return res.data;
    },
  });
}

export function useCreateBotOpenApiRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, BotRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotRuleInput) => (await serviceApiV3.post(botPath(domainId), body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: botOpenApiKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateBotOpenApiRule(
  domainId: string,
  ruleId: string,
  options?: UseMutationOptions<unknown, unknown, BotRuleInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotRuleInput) =>
      (await serviceApiV3.put(botPath(domainId, `/${ruleId}`), body)).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: botOpenApiKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteBotOpenApiRule(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) =>
      (await serviceApiV3.delete(botPath(domainId, `/${id}`))).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: botOpenApiKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}
